import { ChiffrageForm } from './components/ChiffrageForm';
import { ChiffrageTable } from './components/ChiffrageTable';
import { useChiffrages } from './hooks/useChiffrages';
import { checkHealth, isApiConfigured, getApiUrl } from './services/api';
import { useState, useEffect } from 'react';
import './styles/index.css';

// Hook pour le thème
function useTheme() {
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        const saved = localStorage.getItem('theme');
        return (saved === 'dark' || saved === 'light') ? saved : 'light';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

    return { theme, toggleTheme };
}

function App() {
    const {
        chiffrages,
        loading,
        error,
        lastRefresh,
        refresh,
        filterUnivers,
        setFilterUnivers,
        filterStatut,
        setFilterStatut,
        searchQuery,
        setSearchQuery,
    } = useChiffrages({ autoRefreshInterval: 10000 });

    const [serverOnline, setServerOnline] = useState(true);
    const apiConfigured = isApiConfigured();
    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
        if (apiConfigured) {
            checkHealth().then(setServerOnline);
            const interval = setInterval(() => {
                checkHealth().then(setServerOnline);
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [apiConfigured]);

    // Affichage d'erreur si l'API n'est pas configurée en production
    if (!apiConfigured) {
        return (
            <div className="app">
                <header className="app-header">
                    <div className="header-content">
                        <div className="header-brand">
                            <div className="logo">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                    <path d="M2 17l10 5 10-5" />
                                    <path d="M2 12l10 5 10-5" />
                                </svg>
                            </div>
                            <div className="brand-text">
                                <h1>Chiffrage Design</h1>
                                <p>Calcul des jours/homme par rôle</p>
                            </div>
                        </div>
                    </div>
                </header>
                <main className="app-main">
                    <div className="error-banner config-error">
                        <h2>⚠️ Configuration requise</h2>
                        <p>La variable d'environnement <code>VITE_API_URL</code> n'est pas définie.</p>
                        <p>Pour Cloudflare Pages, ajoutez cette variable dans <strong>Settings → Environment Variables</strong> :</p>
                        <pre>VITE_API_URL=https://votre-backend.onrender.com</pre>
                        <p>Puis redéployez l'application.</p>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="app">
            <header className="app-header">
                <div className="header-content">
                    <div className="header-brand">
                        <div className="logo">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                <path d="M2 17l10 5 10-5" />
                                <path d="M2 12l10 5 10-5" />
                            </svg>
                        </div>
                        <div className="brand-text">
                            <h1>Chiffrage Design</h1>
                            <p>Calcul des jours/homme par rôle</p>
                        </div>
                    </div>
                    <div className="header-actions">
                        <button
                            className="theme-toggle"
                            onClick={toggleTheme}
                            title={theme === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair'}
                        >
                            {theme === 'light' ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="5" />
                                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                                </svg>
                            )}
                        </button>
                        <span className={`status-indicator ${serverOnline ? 'online' : 'offline'}`}>
                            {serverOnline ? 'Connecté' : 'Hors ligne'}
                        </span>
                    </div>
                </div>
            </header>

            <main className="app-main">
                {!serverOnline && (
                    <div className="error-banner">
                        ⚠️ Impossible de se connecter au serveur backend ({getApiUrl()}).
                    </div>
                )}

                {error && (
                    <div className="error-banner">
                        ❌ {error}
                    </div>
                )}

                <ChiffrageForm onCreated={refresh} />

                <ChiffrageTable
                    chiffrages={chiffrages}
                    loading={loading}
                    lastRefresh={lastRefresh}
                    onRefresh={refresh}
                    filterUnivers={filterUnivers}
                    setFilterUnivers={setFilterUnivers}
                    filterStatut={filterStatut}
                    setFilterStatut={setFilterStatut}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
            </main>

            <footer className="app-footer">
                <p>Chiffrage Design © {new Date().getFullYear()} — Outil interne équipe Design</p>
            </footer>
        </div>
    );
}

export default App;
